/**
 * Offline Queue Service
 * Stores facility submissions in IndexedDB for offline support
 * Auto-syncs when connection is restored
 */

import { telegram } from './telegram';

export interface QueuedSubmission {
  id: string;  // Client-generated UUID for duplicate prevention
  facilityType: string;
  name: string;
  apiNumber: string | null;  // API number for the facility
  operator: string | null;
  latitude: number;
  longitude: number;
  county: string | null;
  notes: string | null;
  photoBase64: string | null;  // Compressed photo stored as base64
  photoSize: number;
  status: 'queued' | 'syncing' | 'synced' | 'failed';
  errorMessage?: string;
  retryCount: number;
  createdAt: string;
  lastSyncAttempt?: string;
}

const DB_NAME = 'ziawellnav-submissions';
const DB_VERSION = 1;
const STORE_NAME = 'submissions';
// Retry delays with increasing backoff - no max limit
const RETRY_DELAYS = [5000, 15000, 30000, 60000, 120000]; // 5s, 15s, 30s, 1min, 2min

class OfflineQueueService {
  private db: IDBDatabase | null = null;
  private isInitialized = false;
  private isSyncing = false;
  private syncListeners: Set<(count: number) => void> = new Set();

  async init(): Promise<void> {
    if (this.isInitialized) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        console.log('✅ IndexedDB initialized for offline submissions');
        
        // Set up connection listeners
        this.setupConnectionListeners();
        
        // Try to sync any pending items
        this.syncPending();
        
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          console.log('📦 Created submissions object store');
        }
      };
    });
  }

  private setupConnectionListeners(): void {
    window.addEventListener('online', () => {
      console.log('🌐 Connection restored - syncing pending submissions');
      this.syncPending();
    });

    // Also try to sync when app becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        this.syncPending();
      }
    });
  }

  /**
   * Queue a new submission for upload
   */
  async queueSubmission(submission: Omit<QueuedSubmission, 'id' | 'status' | 'retryCount' | 'createdAt'>): Promise<string> {
    await this.init();

    const id = crypto.randomUUID();
    const queuedSubmission: QueuedSubmission = {
      ...submission,
      id,
      status: 'queued',
      retryCount: 0,
      createdAt: new Date().toISOString()
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(queuedSubmission);

      request.onsuccess = () => {
        console.log('📥 Queued submission:', id);
        this.notifyListeners();
        
        // Try to sync immediately if online
        if (navigator.onLine) {
          this.syncPending();
        }
        
        resolve(id);
      };

      request.onerror = () => {
        console.error('❌ Failed to queue submission:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all pending submissions
   */
  async getPendingSubmissions(): Promise<QueuedSubmission[]> {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('status');
      
      const results: QueuedSubmission[] = [];
      const request = index.openCursor(IDBKeyRange.bound('failed', 'queued'));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          if (cursor.value.status === 'queued' || cursor.value.status === 'failed') {
            results.push(cursor.value);
          }
          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get count of pending submissions
   */
  async getPendingCount(): Promise<number> {
    const pending = await this.getPendingSubmissions();
    return pending.length;
  }

  /**
   * Update submission status
   */
  private async updateStatus(id: string, status: QueuedSubmission['status'], errorMessage?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const submission = getRequest.result as QueuedSubmission;
        if (submission) {
          submission.status = status;
          submission.lastSyncAttempt = new Date().toISOString();
          if (errorMessage) {
            submission.errorMessage = errorMessage;
          }
          if (status === 'failed') {
            submission.retryCount++;
          }

          const putRequest = store.put(submission);
          putRequest.onsuccess = () => {
            this.notifyListeners();
            resolve();
          };
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Remove synced submission
   */
  private async removeSubmission(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => {
        this.notifyListeners();
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Reset retry count for manual retry
   */
  private async resetRetryCount(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const submission = getRequest.result as QueuedSubmission;
        if (submission) {
          submission.retryCount = 0;
          submission.errorMessage = undefined;
          const putRequest = store.put(submission);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Sync all pending submissions to server
   */
  async syncPending(): Promise<{ synced: number; failed: number }> {
    if (this.isSyncing || !navigator.onLine) {
      return { synced: 0, failed: 0 };
    }

    this.isSyncing = true;
    let synced = 0;
    let failed = 0;

    try {
      const pending = await this.getPendingSubmissions();
      console.log(`🔄 Syncing ${pending.length} pending submissions...`);

      for (const submission of pending) {
        try {
          await this.updateStatus(submission.id, 'syncing');
          await this.uploadSubmission(submission);
          await this.removeSubmission(submission.id);
          synced++;
          console.log(`✅ Synced submission: ${submission.id}`);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          await this.updateStatus(submission.id, 'failed', errorMessage);
          failed++;
          console.error(`❌ Failed to sync ${submission.id}:`, errorMessage);

          // Schedule retry with exponential backoff
          const delay = RETRY_DELAYS[Math.min(submission.retryCount, RETRY_DELAYS.length - 1)];
          setTimeout(() => this.syncPending(), delay);
        }
      }

      if (synced > 0) {
        telegram.hapticFeedback('success');
      }

    } finally {
      this.isSyncing = false;
    }

    return { synced, failed };
  }

  /**
   * Sync a single submission by ID
   */
  async syncSingleItem(id: string): Promise<{ success: boolean; error?: string }> {
    if (!navigator.onLine) {
      return { success: false, error: 'Offline - will sync when connected' };
    }

    try {
      const submission = await this.getSubmissionById(id);
      if (!submission) {
        return { success: false, error: 'Submission not found' };
      }

      // Reset retry count for manual retry
      await this.resetRetryCount(id);
      await this.updateStatus(id, 'syncing');
      await this.uploadSubmission(submission);
      await this.removeSubmission(id);
      
      console.log(`✅ Single-item sync success: ${id}`);
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      await this.updateStatus(id, 'failed', errorMessage);
      console.error(`❌ Single-item sync failed: ${id}`, errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get a single submission by ID
   */
  async getSubmissionById(id: string): Promise<QueuedSubmission | null> {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Check if currently syncing
   */
  get syncing(): boolean {
    return this.isSyncing;
  }

  /**
   * Upload a single submission to the server
   */
  private async uploadSubmission(submission: QueuedSubmission): Promise<void> {
    const userId = telegram.getUserId();
    console.log('🔐 Telegram User ID:', userId);
    
    if (!userId) {
      console.error('❌ No Telegram User ID - user not authenticated');
      throw new Error('User not authenticated - please open from Telegram');
    }

    console.log('📤 Uploading submission:', {
      id: submission.id,
      facilityType: submission.facilityType,
      name: submission.name,
      hasPhoto: !!submission.photoBase64
    });

    const formData = new FormData();
    formData.append('clientId', submission.id);
    formData.append('facilityType', submission.facilityType);
    formData.append('name', submission.name);
    if (submission.apiNumber) formData.append('apiNumber', submission.apiNumber);
    if (submission.operator) formData.append('operator', submission.operator);
    formData.append('latitude', submission.latitude.toString());
    formData.append('longitude', submission.longitude.toString());
    if (submission.county) formData.append('county', submission.county);
    if (submission.notes) formData.append('notes', submission.notes);

    // Convert base64 photo to blob for upload
    if (submission.photoBase64) {
      const base64Data = submission.photoBase64.replace(/^data:image\/\w+;base64,/, '');
      const binaryData = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
      const photoBlob = new Blob([binaryData], { type: 'image/jpeg' });
      formData.append('photo', photoBlob, 'photo.jpg');
    }

    console.log('📡 POST /api/submissions with User-Id:', userId);
    
    const response = await fetch('/api/submissions', {
      method: 'POST',
      headers: {
        'X-Telegram-User-Id': userId
      },
      body: formData
    });

    console.log('📥 Response status:', response.status);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      console.error('❌ Upload failed:', error);
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    
    const result = await response.json();
    console.log('✅ Upload success:', result);
  }

  /**
   * Subscribe to pending count changes
   */
  onPendingCountChange(callback: (count: number) => void): () => void {
    this.syncListeners.add(callback);
    return () => this.syncListeners.delete(callback);
  }

  private async notifyListeners(): Promise<void> {
    const count = await this.getPendingCount();
    this.syncListeners.forEach(cb => cb(count));
  }

  /**
   * Get all submissions (for debugging)
   */
  async getAllSubmissions(): Promise<QueuedSubmission[]> {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all submissions (for debugging)
   */
  async clearAll(): Promise<void> {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        this.notifyListeners();
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }
}

export const offlineQueue = new OfflineQueueService();
